import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  MobileActions,
  RadioCards,
  RowMenu,
  RowsSkeleton,
  Window,
  useIsMobile,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { ListFooter, useServerList } from "../ui/list";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts, type Label } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Field, FormActions, Notice, PageHead, Switch, TextField, useToast } from "../ui/kit";
import { PageState, ServiceState } from "../ui/pieces";
import {
  audienceKey,
  audiences,
  availabilityText,
  employeeHref,
  employeeLimits,
  employeeTabs,
  jobRoles,
  jobText,
  policyText,
  relationLine,
  relationSummary,
  riskKey,
  statusText,
  termKindKey,
  toolState,
  type Capabilities,
  type Employee,
  type EmployeeRow,
  type EmployeeTab,
  type OrgHead,
  type Pack,
  type Relation,
  type Term,
  type Tool,
} from "./employees";
import { plural } from "./organizations";
import { Check, ConfirmWindow } from "./PersonActions";
import { SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";
import own from "./employees.module.css";

// R5 · Empresa › Empleados IA (§5.6): lista, alta, ficha con Datos · Capacidades · Conocimiento y sus ventanas (§6).
// Los códigos internos del empleado, de las acciones y de los paquetes viajan para operar pero no se muestran (D7).

type SectionTab = "employees" | "knowledge" | "approvals" | "policies";

/** Encabezado «Empleados IA» con su descripción, su acción y las pestañas de la sección (C03). */
export function EmployeesHead({
  orgId,
  current,
  sub,
  children,
}: {
  orgId: number;
  current: SectionTab;
  sub?: string;
  children?: ReactNode;
}) {
  const { L } = useTexts();
  const tabs: { key: SectionTab; label: string; href: string }[] = [
    { key: "employees", label: L("ADM_EMP_TAB_EMPLOYEES"), href: `/Organizations/Empleados?id=${orgId}` },
    { key: "knowledge", label: L("ADM_EMP_TAB_KNOWLEDGE"), href: `/Organizations/Conocimiento?id=${orgId}` },
    { key: "approvals", label: L("ADM_EMP_TAB_APPROVALS"), href: `/Organizations/Aprobaciones?id=${orgId}` },
    { key: "policies", label: L("ADM_EMP_TAB_POLICIES"), href: `/Organizations/Aprobaciones/Politicas?id=${orgId}` },
  ];
  return (
    <>
      <PageHead title={L("ADM_ORG_EMPLOYEES")} sub={sub}>
        {children}
      </PageHead>
      <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_ORG_EMPLOYEES")} data-part="pestanas">
        {tabs.map((tab) => (
          <a key={tab.key} href={tab.href} aria-current={tab.key === current ? "page" : undefined}>
            {tab.label}
          </a>
        ))}
      </nav>
    </>
  );
}

/** Estado de página cuando la lectura falla: sin permiso (C40) o no disponible con «Reintentar» (nunca 0, H-3). */
export function Failed({ head, status, retry }: { head: ReactNode; status: number | undefined; retry: () => void }) {
  const { L } = useTexts();
  return (
    <PageState
      head={head}
      title={status === 403 ? L("ADM_FORBIDDEN") : L("ADM_UNAVAILABLE")}
      line={status === 403 ? L("ADM_FORBIDDEN_LINE") : L("ADM_UNAVAILABLE_LINE")}
      action={
        status === 403 ? undefined : (
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        )
      }
    />
  );
}

/** Aviso C14 con «Reintentar» para un bloque que no se pudo leer (la pantalla sigue). */
export function BlockFailed({ retry }: { retry: () => void }) {
  const { L } = useTexts();
  return (
    <Notice
      tone="error"
      action={
        <button type="button" className="tm-btn secondary" onClick={retry}>
          {L("ADM_RETRY")}
        </button>
      }
    >
      {L("ADM_UNAVAILABLE_LINE")}
    </Notice>
  );
}

/** C20 · avatar de empleado IA (user-cog, D3). */
export function EmployeeAvatar({ large }: { large?: boolean }) {
  return (
    <span className={`${own.avatar}${large ? ` ${own.avatarLarge}` : ""}`} aria-hidden="true">
      <ChatIcon name="user-cog" />
    </span>
  );
}

/** El mensaje de un rechazo del BFF (la clave que manda el servidor) o el genérico. */
export const replyMessage = (L: Label, body: { messageKey?: string } | null | undefined) =>
  L(body?.messageKey ?? "ADMIN_ERR_GENERIC");

/**
 * Activar y desactivar un empleado IA (D8): desactivar confirma con `desactivar-emp`; activar aplica al instante con
 * toast y «Deshacer». Se usa en la lista y en la ficha (E48).
 */
function useEmployeeStatus(orgId: number, onChanged: () => void) {
  const { L } = useTexts();
  const toast = useToast();
  const [confirming, setConfirming] = useState<Pick<EmployeeRow, "id" | "name"> | null>(null);
  const set = async (employee: Pick<EmployeeRow, "id" | "name">, active: boolean, undo = true): Promise<boolean> => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/employees/${employee.id}/status`,
      { status: active ? "ENABLED" : "DISABLED" },
    );
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(replyMessage(L, reply.body));
      return false;
    }
    onChanged();
    toast.show(
      formatText(L(active ? "ADM_PPL_ACTIVATED" : "ADM_PPL_DEACTIVATED"), employee.name),
      undo ? { label: L("ADM_UNDO"), run: () => void set(employee, !active, false) } : undefined,
    );
    return true;
  };
  return {
    activate: (employee: Pick<EmployeeRow, "id" | "name">) => void set(employee, true),
    deactivate: (employee: Pick<EmployeeRow, "id" | "name">) => setConfirming(employee),
    window: (
      <ConfirmWindow
        open={!!confirming}
        title={formatText(L("ADM_PPL_DEACTIVATE_TITLE"), confirming?.name ?? "")}
        line={L("ADM_EMP_DEACTIVATE_LINE")}
        action={L("ADM_PPL_DEACTIVATE")}
        onClose={() => setConfirming(null)}
        onConfirm={() => (confirming ? set(confirming, false) : Promise.resolve(true))}
      />
    ),
  };
}

/**
 * `emp-empleados` · Empleados IA › Empleados: tabla C28 (C17 en el celular) con avatar, trabajo, estado y disponibilidad;
 * ⋮ Editar · Capacidades · Conocimiento · Desactivar… (C12) o Activar (C13). R7 · B8 (AR13): paginada en EmployeesIA de
 * 10 en 10 con la página en la URL; el contador y el paginado salen del mismo total (E100).
 */
export function EmployeesPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const list = useServerList<EmployeeRow>(`/api/admin/organizations/${orgId}/employees`);
  const status = useEmployeeStatus(orgId, () => list.reload());
  useOpenOrg((list.extra?.organization as OrgHead | undefined) ?? null);
  useTitle(`${L("ADM_ORG_EMPLOYEES")} · ${L("ADM_EMP_TAB_EMPLOYEES")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const newHref = `/Organizations/Empleado?id=${orgId}`;
  // C21 · en el celular, «+» redondo junto al título (lista); en escritorio, el botón con su texto.
  const action = (
    <>
      <a className={`tm-btn primary ${ui.desktopInline}`} href={newHref}>
        {L("ADM_EMP_NEW")}
      </a>
      <a className={ui.fab} href={newHref} aria-label={L("ADM_EMP_NEW")} title={L("ADM_EMP_NEW")}>
        <ChatIcon name="plus" />
      </a>
    </>
  );
  const head = (
    <EmployeesHead orgId={orgId} current="employees" sub={L("ADM_EMP_AUDIT_NOTE")}>
      {action}
    </EmployeesHead>
  );
  if (list.state !== "ready") {
    if (list.state !== "loading")
      return <Failed head={head} status={list.state === "forbidden" ? 403 : 502} retry={list.reload} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={3} table={!mobile} />
      </>
    );
  }
  const menu = (row: EmployeeRow) => [
    { label: L("ADM_EMP_EDIT"), onSelect: () => navigate(employeeHref(orgId, row.id)) },
    { label: L("ADM_EMP_TAB_CAPABILITIES"), onSelect: () => navigate(employeeHref(orgId, row.id, "capacidades")) },
    { label: L("ADM_EMP_TAB_KNOWLEDGE_EMP"), onSelect: () => navigate(employeeHref(orgId, row.id, "conocimiento")) },
    row.active
      ? { label: L("ADM_PPL_DEACTIVATE_MENU"), danger: true, onSelect: () => status.deactivate(row) }
      : { label: L("ADM_PPL_ACTIVATE"), onSelect: () => status.activate(row) },
  ];
  return (
    <>
      {head}
      {list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_EMP_EMPTY")}</strong>
          <span>{L("ADM_EMP_EMPTY_LINE")}</span>
          <a className="tm-btn primary" href={newHref}>
            {L("ADM_EMP_NEW")}
          </a>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_EMP_COUNT_ONE", "ADM_EMP_COUNT")}</Counter>
          {mobile ? (
            // C17 · avatar de empleado IA, nombre y «trabajo · estado · disponibilidad»; la fila abre la ficha.
            <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_EMP_TAB_EMPLOYEES")} data-part="lista">
              {list.items.map((row) => (
                <li key={row.id} className={ui.row}>
                  <a className={ui.rowLinkArea} href={employeeHref(orgId, row.id)}>
                    <EmployeeAvatar large />
                    <span className={ui.rowText}>
                      <span className={ui.line1}>
                        <strong>{row.name}</strong>
                      </span>
                      {/* AR20 · dos líneas: «Ventas · Inactivo»; la disponibilidad está en la ficha. */}
                      <span className={ui.line2}>{[jobText(L, row), statusText(L, row.active)].join(" · ")}</span>
                    </span>
                    <span className={ui.rowEnd} aria-hidden="true">
                      <ChatIcon name="chevron-right" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className={own.fit}>
              <DataTable
                label={L("ADM_EMP_TAB_EMPLOYEES")}
                rows={list.items}
                rowKey={(row) => row.id}
                href={(row) => employeeHref(orgId, row.id)}
                columns={[
                  {
                    key: "name",
                    title: L("ADM_EMP_COL_EMPLOYEE"),
                    render: (row) => (
                      <span className={own.nameCell} title={row.name}>
                        <EmployeeAvatar />
                        <span>{row.name}</span>
                      </span>
                    ),
                  },
                  { key: "job", title: L("ADM_EMP_COL_JOB"), render: (row) => jobText(L, row) },
                  { key: "status", title: L("ADM_EMP_COL_STATUS"), render: (row) => statusText(L, row.active) },
                  {
                    key: "availability",
                    title: L("ADM_EMP_COL_AVAILABILITY"),
                    render: (row) => availabilityText(L, row.available),
                  },
                  {
                    key: "menu",
                    title: "",
                    render: (row) => (
                      <RowMenu
                        label={formatText(L("ADM_PPL_ACTIONS_FOR"), row.name)}
                        title={row.name}
                        items={menu(row)}
                      />
                    ),
                  },
                ]}
                mobile={(row) => ({
                  title: row.name,
                  line: [jobText(L, row), statusText(L, row.active)].join(" · "),
                })}
              />
            </div>
          )}
          <ListFooter list={list} />
        </>
      )}
      {status.window}
    </>
  );
}

type Draft = { jobCode: string; name: string; objective: string; instructions: string; style: string };

const draftOf = (employee: Employee | null): Draft => ({
  jobCode: employee?.jobCode ?? "VENTAS",
  name: employee?.name ?? "",
  objective: employee?.objective ?? "",
  instructions: employee?.instructions ?? "",
  style: employee?.communicationStyle ?? "",
});

type FieldErrors = Partial<Record<"name" | "objective" | "instructions" | "communicationStyle", string>>;

/** Validación de hoy: nombre de 2 a 150; los demás con su largo máximo (E81: el error se va al corregir). */
function validate(L: Label, draft: Draft): FieldErrors {
  const errors: FieldErrors = {};
  const name = draft.name.trim();
  if (name.length < 2 || name.length > employeeLimits.name) errors.name = L("ORGEMPF_ERR_NAME");
  return errors;
}

/**
 * Formulario de Datos (alta y edición, E48): Trabajo en píldoras de una opción (C07/C08; un trabajo que no es de los
 * cuatro se conserva como opción), Nombre, Objetivo, Instrucciones y Estilo de comunicación con los límites de hoy.
 */
function EmployeeFields({
  draft,
  errors,
  extraJob,
  onChange,
}: {
  draft: Draft;
  errors: FieldErrors;
  extraJob: { code: string; label: string } | null;
  onChange: (change: Partial<Draft>) => void;
}) {
  const { L } = useTexts();
  const options = [
    ...jobRoles.map((role) => ({ value: role.code as string, label: L(role.key) })),
    ...(extraJob ? [{ value: extraJob.code, label: extraJob.label }] : []),
  ];
  const textarea = (id: string, key: "instructions" | "style", label: string, placeholder: string, max: number) => (
    <div className={ui.full}>
      <Field id={id} label={label} optional={L("CFG1_R1_OPTIONAL")}>
        <textarea
          id={id}
          className={styles.textarea}
          value={draft[key]}
          maxLength={max}
          placeholder={placeholder}
          rows={4}
          onChange={(event) => onChange({ [key]: event.target.value })}
        />
      </Field>
    </div>
  );
  return (
    <>
      <div className={`${ui.full} ${styles.field}`} data-part="trabajo">
        <span className={styles.fieldTitle} id="emp-job">
          {L("ADM_EMP_JOB")}
        </span>
        <div className={styles.choice} role="radiogroup" aria-labelledby="emp-job">
          {options.map((item) => (
            <button
              key={item.value}
              type="button"
              role="radio"
              className="tm-pill"
              aria-checked={draft.jobCode === item.value ? "true" : "false"}
              aria-pressed={draft.jobCode === item.value ? "true" : "false"}
              onClick={() => onChange({ jobCode: item.value })}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <TextField
        id="emp-name"
        field="name"
        label={L("ADM_EMP_NAME")}
        help={L("ADM_EMP_NAME_HELP")}
        value={draft.name}
        error={errors.name}
        maxLength={employeeLimits.name}
        placeholder={L("ADM_EMP_NAME_EXAMPLE")}
        onChange={(name) => onChange({ name })}
      />
      <TextField
        id="emp-objective"
        field="objective"
        label={L("ADM_EMP_OBJECTIVE")}
        optional={L("CFG1_R1_OPTIONAL")}
        value={draft.objective}
        maxLength={employeeLimits.objective}
        placeholder={L("ADM_EMP_OBJECTIVE_EXAMPLE")}
        onChange={(objective) => onChange({ objective })}
      />
      {textarea(
        "emp-instructions",
        "instructions",
        L("ADM_EMP_INSTRUCTIONS"),
        L("ADM_EMP_INSTRUCTIONS_EXAMPLE"),
        employeeLimits.instructions,
      )}
      {textarea("emp-style", "style", L("ADM_EMP_STYLE"), L("ADM_EMP_STYLE_EXAMPLE"), employeeLimits.style)}
    </>
  );
}

const bodyOf = (draft: Draft) => ({
  jobRoleCode: draft.jobCode,
  name: draft.name.trim(),
  objective: draft.objective.trim() || null,
  instructions: draft.instructions.trim() || null,
  communicationStyle: draft.style.trim() || null,
});

/**
 * `emp-empleado-nuevo` · formulario de página (ancho angosto, C21 en el celular). Al crear: toast y la ficha del
 * empleado en Capacidades (paso siguiente, C43). El empleado nace inactivo para revisarlo antes de activarlo (como hoy).
 */
export function EmployeeNewPage({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const toast = useToast();
  const navigate = useNavigate();
  const head = useAiData<{ organization: OrgHead }>(`/api/admin/organizations/${orgId}/head`);
  const [draft, setDraft] = useState<Draft>(draftOf(null));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useOpenOrg(head.data?.organization ?? null);
  useTitle(L("ADM_EMP_NEW_TITLE"));
  if (head.state === "missing") return <OrgNotFound />;
  const back = `/Organizations/Empleados?id=${orgId}`;
  const change = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    // E81: el error de un campo se borra en cuanto el valor es válido.
    if (errors.name && !validate(L, next).name) setErrors({});
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const found = validate(L, draft);
    setErrors(found);
    if (Object.keys(found).length) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>(".tm [aria-invalid='true']")?.focus());
      return;
    }
    setBusy(true);
    setError(null);
    const reply = await postJson<{ saved?: boolean; employeeId?: number; field?: string; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/employees`,
      bodyOf(draft),
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved && reply.body.employeeId) {
      toast.show(L("ADM_EMP_CREATED"));
      navigate(employeeHref(orgId, reply.body.employeeId, "capacidades"));
      return;
    }
    if (reply.body?.field === "name") setErrors({ name: replyMessage(L, reply.body) });
    else setError(replyMessage(L, reply.body));
  };
  const actions = (
    <>
      <a className="tm-btn secondary" href={back}>
        {L("ACTION_CANCEL")}
      </a>
      <button type="submit" form="emp-new" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
        {L("ADM_EMP_CREATE")}
      </button>
    </>
  );
  return (
    <div className={styles.formPage}>
      <PageHead title={L("ADM_EMP_NEW_TITLE")} back={{ href: back, label: L("ADM_ORG_EMPLOYEES") }}>
        <span className={ui.desktopOnly}>{actions}</span>
      </PageHead>
      <form id="emp-new" className={`tm-form ${ui.formGrid}`} noValidate onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className={ui.formSummary}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}
        <EmployeeFields draft={draft} errors={errors} extraJob={null} onChange={change} />
        <div className={`${ui.mobileOnly} ${ui.full}`}>
          <FormActions>{actions}</FormActions>
        </div>
      </form>
    </div>
  );
}

const tabKey: Record<EmployeeTab, string> = {
  datos: "ADM_EMP_TAB_DATA",
  capacidades: "ADM_EMP_TAB_CAPABILITIES",
  conocimiento: "ADM_EMP_TAB_KNOWLEDGE_EMP",
};

/**
 * Ficha del empleado IA (`emp-empleado`, `emp-empleado-capacidades`, `emp-empleado-conocimiento`): ← a Empleados IA,
 * título con «Empleado IA» y su estado, «{trabajo} · {disponibilidad}», ⋮ Desactivar…/Activar y las pestañas en la URL.
 */
export function EmployeePage({ orgId, employeeId, tab }: { orgId: number; employeeId: number; tab: EmployeeTab }) {
  const { L } = useTexts();
  const path =
    tab === "capacidades"
      ? `/api/admin/organizations/${orgId}/employees/${employeeId}/capabilities`
      : tab === "conocimiento"
        ? `/api/admin/organizations/${orgId}/employees/${employeeId}/knowledge`
        : `/api/admin/organizations/${orgId}/employees/${employeeId}`;
  const data = useAiData<{ organization: OrgHead; employee: Employee }>(path);
  const status = useEmployeeStatus(orgId, () => void data.reload());
  useOpenOrg(data.data?.organization ?? null);
  const employee = data.data?.employee ?? null;
  useTitle(`${employee?.name ?? L("ADM_ORG_EMPLOYEES")} · ${L(tabKey[tab])}`);
  const back = { href: `/Organizations/Empleados?id=${orgId}`, label: L("ADM_ORG_EMPLOYEES") };
  const tabs = (
    <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_EMP_FICHA_TABS")} data-part="pestanas">
      {employeeTabs.map((value) => (
        <a key={value} href={employeeHref(orgId, employeeId, value)} aria-current={value === tab ? "page" : undefined}>
          {L(tabKey[value])}
        </a>
      ))}
    </nav>
  );
  if (data.state === "missing") return <EmployeeMissing orgId={orgId} />;
  if (!employee) {
    const head = (
      <>
        <PageHead title={L("ADM_ORG_EMPLOYEES")} back={back} />
        {tabs}
      </>
    );
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={4} table />
      </>
    );
  }
  const menu = [
    employee.active
      ? { label: L("ADM_PPL_DEACTIVATE_MENU"), danger: true, onSelect: () => status.deactivate(employee) }
      : { label: L("ADM_PPL_ACTIVATE"), onSelect: () => status.activate(employee) },
  ];
  const headFor = (primary: ReactNode) => (
    <>
      <div className={own.ficha}>
        <PageHead
          title={employee.name}
          back={back}
          titleTag={
            <>
              <span className={`tm-tag ${own.aiTag}`}>{L("ADM_EMP_TAG")}</span>
              <span className="tm-tag">{statusText(L, employee.active)}</span>
            </>
          }
          sub={`${jobText(L, employee)} · ${availabilityText(L, employee.available)}`}
        >
          <RowMenu label={L("ADM_MORE_ACTIONS")} title={employee.name} items={menu} />
          {primary ? <span className={ui.desktopInline}>{primary}</span> : null}
        </PageHead>
      </div>
      {tabs}
    </>
  );
  return (
    <>
      {tab === "datos" ? (
        <DataTab
          key={JSON.stringify(employee)}
          orgId={orgId}
          employee={employee}
          headFor={headFor}
          onSaved={() => void data.reload()}
        />
      ) : tab === "capacidades" ? (
        <CapabilitiesTab
          orgId={orgId}
          data={data.data as unknown as Capabilities}
          headFor={headFor}
          reload={() => void data.reload()}
        />
      ) : (
        <KnowledgeTab
          key={JSON.stringify((data.data as unknown as EmployeeKnowledge).policy)}
          orgId={orgId}
          data={data.data as unknown as EmployeeKnowledge}
          headFor={headFor}
          reload={() => void data.reload()}
        />
      )}
      {status.window}
    </>
  );
}

function EmployeeMissing({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  return (
    <PageState
      head={
        <PageHead
          title={L("ADM_ORG_EMPLOYEES")}
          back={{ href: `/Organizations/Empleados?id=${orgId}`, label: L("ADM_ORG_EMPLOYEES") }}
        />
      }
      title={L("ADM_EMP_MISSING")}
      line={L("ADM_EMP_MISSING_LINE")}
      action={
        <a className="tm-btn secondary" href={`/Organizations/Empleados?id=${orgId}`}>
          {L("ADM_EMP_BACK")}
        </a>
      }
    />
  );
}

/** Pestaña Datos: el mismo formulario del alta con «Guardar» (toast «{nombre} guardado»). */
function DataTab({
  orgId,
  employee,
  headFor,
  onSaved,
}: {
  orgId: number;
  employee: Employee;
  headFor: (primary: ReactNode) => ReactNode;
  onSaved: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(draftOf(employee));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const known = jobRoles.some((role) => role.code === employee.jobCode);
  const extraJob =
    !known && employee.jobCode ? { code: employee.jobCode, label: employee.job ?? employee.jobCode } : null;
  const change = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (errors.name && !validate(L, next).name) setErrors({});
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const found = validate(L, draft);
    setErrors(found);
    if (Object.keys(found).length) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>(".tm [aria-invalid='true']")?.focus());
      return;
    }
    setBusy(true);
    setError(null);
    const reply = await postJson<{ saved?: boolean; field?: string; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/employees/${employee.id}`,
      bodyOf(draft),
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(formatText(L("ADM_EMP_SAVED"), draft.name.trim()));
      onSaved();
      return;
    }
    // Nunca se pierde lo escrito: el motivo queda arriba o en su campo.
    if (reply.body?.field === "name") setErrors({ name: replyMessage(L, reply.body) });
    else setError(replyMessage(L, reply.body));
  };
  const save = (
    <button type="submit" form="emp-data" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
      {L("ADM_PPL_SAVE")}
    </button>
  );
  return (
    <>
      {headFor(save)}
      <form
        id="emp-data"
        className={`tm-form ${ui.formGrid} ${own.formWide}`}
        noValidate
        onSubmit={(e) => void submit(e)}
      >
        {error ? (
          <div className={ui.formSummary}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}
        <EmployeeFields draft={draft} errors={errors} extraJob={extraJob} onChange={change} />
      </form>
      <MobileActions>{save}</MobileActions>
    </>
  );
}

// ---- Capacidades ----

/**
 * Pestaña Capacidades: Paquetes (nivel en selector de celda con «Deshacer», quitar confirma), Trabaja con (frase y lista
 * con «Quitar» que confirma) y Acciones (riesgo y estado C55; bloquear confirma, desbloquear y permitir con «Deshacer»).
 */
function CapabilitiesTab({
  orgId,
  data,
  headFor,
  reload,
}: {
  orgId: number;
  data: Capabilities;
  headFor: (primary: ReactNode) => ReactNode;
  reload: () => void;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const employee = data.employee;
  const base = `/api/admin/organizations/${orgId}/employees/${employee.id}`;
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState<Pack | null>(null);
  const [relating, setRelating] = useState(false);
  const [unrelating, setUnrelating] = useState<Relation | null>(null);
  const [blocking, setBlocking] = useState<Tool | null>(null);

  /** Una escritura del gobierno; si falla, el motivo en el toast (lo de la pantalla no cambia). */
  const write = async (url: string, body: unknown): Promise<boolean> => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(url, body);
    if (reply.status === 200 && reply.body?.saved) {
      reload();
      return true;
    }
    toast.show(replyMessage(L, reply.body));
    return false;
  };
  const setLevel = async (pack: Pack, access: "READ" | "WRITE", undo = true) => {
    if (!(await write(`${base}/packs/${encodeURIComponent(pack.code)}`, { access }))) return;
    toast.show(
      formatText(L(access === "WRITE" ? "ADM_EMP_PACK_TO_WRITE" : "ADM_EMP_PACK_TO_READ"), pack.name),
      undo ? { label: L("ADM_UNDO"), run: () => void setLevel(pack, pack.access, false) } : undefined,
    );
  };
  const exception = async (tool: Tool, mode: string, toastKey: string, undoMode: string | null) => {
    if (!(await write(`${base}/tools/${encodeURIComponent(tool.code)}`, { mode }))) return false;
    toast.show(
      formatText(L(toastKey), tool.name, employee.name),
      undoMode
        ? {
            label: L("ADM_UNDO"),
            run: () => void write(`${base}/tools/${encodeURIComponent(tool.code)}`, { mode: undoMode }),
          }
        : undefined,
    );
    return true;
  };
  const relationAdd = async (relation: Relation) =>
    write(`${base}/relations`, {
      otherId: relation.otherId,
      relation: relation.kind === "peer" ? "PEER" : "SUPERVISES",
      supervised: relation.kind === "supervised",
      parentId: relation.parentId,
      childId: relation.childId,
    });

  const assign = (
    <button
      type="button"
      className="tm-btn primary"
      disabled={data.available.length === 0}
      title={data.available.length === 0 ? L("ADM_EMP_NO_MORE_PACKS") : undefined}
      onClick={() => setAssigning(true)}
    >
      {L("ADM_EMP_ASSIGN_PACK")}
    </button>
  );
  const levelOptions = [
    { value: "READ", label: L("ADM_EMP_LEVEL_READ") },
    { value: "WRITE", label: L("ADM_EMP_LEVEL_WRITE") },
  ];
  const levelSelect = (pack: Pack) => (
    <div className={own.cellChoice}>
      <ChoiceMenu
        label={formatText(L("ADM_EMP_LEVEL_OF"), pack.name)}
        value={pack.access}
        options={levelOptions}
        onChange={(value) => value !== pack.access && void setLevel(pack, value as "READ" | "WRITE")}
        chevron={<ChatIcon name="chevron-down" />}
        check={<ChatIcon name="check" />}
      />
    </div>
  );
  /**
   * ⋮ de una acción según lo que el servicio permite (CapabilityAdministrationService): bloquear y permitir viven sobre
   * un paquete asignado; sin paquete no hay nada que bloquear ni permitir y la opción dice por qué (E69).
   */
  const toolMenu = (tool: Tool) => {
    if (tool.state === "blocked")
      return [
        {
          label: L("ADM_EMP_UNBLOCK"),
          onSelect: () => void exception(tool, "UNBLOCK", "ADM_EMP_UNBLOCKED", "BLOCK"),
        },
      ];
    if (tool.state === "notGranted")
      return [
        { label: L("ADM_EMP_BLOCK_MENU"), disabled: true, line: L("ADM_EMP_NEEDS_PACK"), onSelect: () => undefined },
      ];
    return [
      ...(tool.state === "needsWrite"
        ? [
            {
              label: L("ADM_EMP_ALLOW"),
              onSelect: () => void exception(tool, "ALLOW", "ADM_EMP_ALLOWED", "REMOVE_ALLOW"),
            },
          ]
        : []),
      ...(tool.allowed
        ? [
            {
              label: L("ADM_EMP_REMOVE_ALLOW"),
              onSelect: () => void exception(tool, "REMOVE_ALLOW", "ADM_EMP_ALLOW_REMOVED", "ALLOW"),
            },
          ]
        : []),
      { label: L("ADM_EMP_BLOCK_MENU"), danger: true, onSelect: () => setBlocking(tool) },
    ];
  };
  const relations = data.relations;
  return (
    <>
      {headFor(assign)}
      <section className={ui.section} aria-labelledby="cap-packs">
        <div className={ui.sectionHead}>
          <div>
            <h2 id="cap-packs" className={ui.subtitle}>
              {L("ADM_EMP_PACKS")}
            </h2>
            <p className={ui.sectionLine}>{L("ADM_EMP_PACKS_LINE")}</p>
          </div>
        </div>
        {data.packs.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_EMP_NO_PACKS")}</strong>
            <span>{L("ADM_EMP_NO_PACKS_LINE")}</span>
            {data.available.length ? (
              <button type="button" className="tm-btn secondary" onClick={() => setAssigning(true)}>
                {L("ADM_EMP_ASSIGN_PACK")}
              </button>
            ) : null}
          </div>
        ) : (
          <SimpleTable
            label={L("ADM_EMP_PACKS")}
            rows={data.packs}
            rowKey={(pack) => pack.code}
            columns={[
              { key: "name", title: L("ADM_EMP_COL_PACK"), className: own.titleCell, render: (pack) => pack.name },
              { key: "level", title: L("ADM_EMP_COL_LEVEL"), render: levelSelect },
              {
                key: "actions",
                title: L("ADM_EMP_COL_GRANTS"),
                numeric: true,
                render: (pack) => pack.actions.toLocaleString(locale),
              },
              {
                key: "menu",
                title: "",
                className: own.endCell,
                render: (pack) => (
                  <RowMenu
                    label={formatText(L("ADM_PPL_ACTIONS_FOR"), pack.name)}
                    title={pack.name}
                    items={[{ label: L("ADM_EMP_REMOVE_MENU"), danger: true, onSelect: () => setRemoving(pack) }]}
                  />
                ),
              },
            ]}
            mobile={(pack) => ({
              title: pack.name,
              line: `${L(pack.access === "WRITE" ? "ADM_EMP_LEVEL_WRITE" : "ADM_EMP_LEVEL_READ")} · ${pack.actions.toLocaleString(locale)}`,
              end: (
                <RowMenu
                  label={formatText(L("ADM_PPL_ACTIONS_FOR"), pack.name)}
                  title={pack.name}
                  items={[
                    {
                      label: L(pack.access === "WRITE" ? "ADM_EMP_TO_READ" : "ADM_EMP_TO_WRITE"),
                      onSelect: () => void setLevel(pack, pack.access === "WRITE" ? "READ" : "WRITE"),
                    },
                    { label: L("ADM_EMP_REMOVE_MENU"), danger: true, onSelect: () => setRemoving(pack) },
                  ]}
                />
              ),
            })}
          />
        )}
      </section>

      <section className={ui.section} aria-labelledby="cap-rel">
        <div className={ui.sectionHead}>
          <div>
            <h2 id="cap-rel" className={ui.subtitle}>
              {L("ADM_EMP_WORKS_WITH")}
            </h2>
            <p className={ui.sectionLine}>
              {relations ? relationSummary(L, locale, relations) : L("ADM_EMP_REL_LINE")}
            </p>
          </div>
          {relations ? (
            <button
              type="button"
              className={own.light}
              disabled={data.candidates.length === 0}
              title={data.candidates.length === 0 ? L("ADM_EMP_NO_CANDIDATES") : undefined}
              onClick={() => setRelating(true)}
            >
              {L("ADM_EMP_ADD_RELATION")}
            </button>
          ) : null}
        </div>
        {!relations ? (
          <BlockFailed retry={reload} />
        ) : relations.length === 0 ? (
          <p className={ui.sectionLine}>{L("ADM_EMP_NO_RELATIONS")}</p>
        ) : (
          <ul className={own.rows} aria-label={L("ADM_EMP_WORKS_WITH")}>
            {relations.map((relation) => {
              const name = relation.name ?? L("ADM_EMP_UNKNOWN");
              return (
                <li key={`${relation.parentId}-${relation.childId}`}>
                  <EmployeeAvatar large />
                  <span className={own.rowText}>
                    <span className={own.rowTitle}>
                      <span>{name}</span>
                    </span>
                    <span className={own.rowLine}>
                      {[relation.job, relationLine(L, relation, employee.name)].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={own.light}
                    aria-label={formatText(L("ADM_EMP_REMOVE_RELATION_FOR"), name)}
                    onClick={() => setUnrelating(relation)}
                  >
                    {L("ADM_EMP_REMOVE")}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className={ui.section} aria-labelledby="cap-tools">
        <div className={ui.sectionHead}>
          <div>
            <h2 id="cap-tools" className={ui.subtitle}>
              {L("ADM_EMP_ACTIONS")}
            </h2>
            <p className={ui.sectionLine}>{L("ADM_EMP_ACTIONS_LINE")}</p>
          </div>
          <a className={`tm-btn secondary ${own.light}`} href={`/Organizations/Aprobaciones/Politicas?id=${orgId}`}>
            {L("ADM_EMP_SEE_POLICIES")}
          </a>
        </div>
        {data.tools.length === 0 ? (
          <p className={ui.sectionLine}>{L("ADM_EMP_NO_ACTIONS")}</p>
        ) : (
          <SimpleTable
            label={L("ADM_EMP_ACTIONS")}
            rows={data.tools}
            rowKey={(tool) => tool.code}
            columns={[
              { key: "name", title: L("ADM_EMP_COL_ACTION"), className: own.titleCell, render: (tool) => tool.name },
              {
                key: "changes",
                title: L("ADM_EMP_COL_CHANGES"),
                render: (tool) => L(tool.changesData ? "ADM_EMP_YES" : "ADM_EMP_NO"),
              },
              { key: "risk", title: L("ADM_EMP_COL_RISK"), render: (tool) => L(riskKey[tool.risk]) },
              {
                key: "state",
                title: L("ADM_EMP_COL_STATE"),
                render: (tool) => <ServiceState tone={toolState(tool).tone}>{L(toolState(tool).key)}</ServiceState>,
              },
              {
                key: "approval",
                title: L("ADM_EMP_COL_APPROVAL"),
                render: (tool) => (tool.state === "blocked" ? "—" : policyText(L, tool.approval)),
              },
              {
                key: "menu",
                title: "",
                className: own.endCell,
                render: (tool) => (
                  <RowMenu
                    label={formatText(L("ADM_PPL_ACTIONS_FOR"), tool.name)}
                    title={tool.name}
                    items={toolMenu(tool)}
                  />
                ),
              },
            ]}
            mobile={(tool) => ({
              title: tool.name,
              tag: <ServiceState tone={toolState(tool).tone}>{L(toolState(tool).key)}</ServiceState>,
              line: [
                L(tool.changesData ? "ADM_EMP_YES" : "ADM_EMP_NO"),
                L(riskKey[tool.risk]),
                tool.state === "blocked" ? null : policyText(L, tool.approval),
              ]
                .filter(Boolean)
                .join(" · "),
              end: (
                <RowMenu
                  label={formatText(L("ADM_PPL_ACTIONS_FOR"), tool.name)}
                  title={tool.name}
                  items={toolMenu(tool)}
                />
              ),
            })}
          />
        )}
      </section>
      {mobile ? <MobileActions>{assign}</MobileActions> : null}

      <AssignPackWindow
        open={assigning}
        employee={employee}
        available={data.available}
        onClose={() => setAssigning(false)}
        onAssign={async (pack, access) => {
          if (!(await write(`${base}/packs/${encodeURIComponent(pack.code)}`, { access }))) return false;
          toast.show(formatText(L("ADM_EMP_PACK_ASSIGNED"), pack.name, employee.name));
          return true;
        }}
      />
      <ConfirmWindow
        open={!!removing}
        title={formatText(L("ADM_EMP_REMOVE_PACK_TITLE"), removing?.name ?? "")}
        line={
          removing
            ? formatText(
                L(removing.actions === 1 ? "ADM_EMP_REMOVE_PACK_LINE_ONE" : "ADM_EMP_REMOVE_PACK_LINE"),
                employee.name,
                removing.actions.toLocaleString(locale),
              )
            : ""
        }
        action={L("ADM_EMP_REMOVE")}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return true;
          const done = await write(`${base}/packs/${encodeURIComponent(removing.code)}/remove`, {});
          if (done) toast.show(formatText(L("ADM_EMP_PACK_REMOVED"), removing.name));
          return done;
        }}
      />
      <RelationWindow
        open={relating}
        employee={employee}
        candidates={data.candidates}
        onClose={() => setRelating(false)}
        onAdd={async (otherId, relation) => {
          const done = await write(`${base}/relations`, { otherId, relation, supervised: false });
          if (done) toast.show(L("ADM_EMP_RELATION_ADDED"));
          return done;
        }}
      />
      <ConfirmWindow
        open={!!unrelating}
        title={formatText(L("ADM_EMP_REMOVE_REL_TITLE"), unrelating?.name ?? L("ADM_EMP_UNKNOWN"))}
        line={unrelating ? removeRelationLine(L, unrelating, employee.name) : ""}
        action={L("ADM_EMP_REMOVE")}
        onClose={() => setUnrelating(null)}
        onConfirm={async () => {
          if (!unrelating) return true;
          const relation = unrelating;
          const done = await write(`${base}/relations/remove`, {
            parentId: relation.parentId,
            childId: relation.childId,
          });
          if (done)
            toast.show(
              L("ADM_EMP_RELATION_REMOVED"),
              relation.kind === "created" || relation.kind === "createdBy"
                ? undefined
                : { label: L("ADM_UNDO"), run: () => void relationAdd(relation) },
            );
          return done;
        }}
      />
      <ConfirmWindow
        open={!!blocking}
        title={formatText(L("ADM_EMP_BLOCK_TITLE"), blocking?.name ?? "", employee.name)}
        line={L("ADM_EMP_BLOCK_LINE")}
        action={L("ADM_EMP_BLOCK")}
        onClose={() => setBlocking(null)}
        onConfirm={async () => (blocking ? exception(blocking, "BLOCK", "ADM_EMP_BLOCKED", "UNBLOCK") : true)}
      />
    </>
  );
}

function removeRelationLine(L: Label, relation: Relation, employeeName: string): string {
  switch (relation.kind) {
    case "supervises":
      return formatText(L("ADM_EMP_REMOVE_REL_SUPERVISES"), employeeName);
    case "supervised":
      return formatText(L("ADM_EMP_REMOVE_REL_SUPERVISED"), employeeName);
    default:
      return L("ADM_EMP_REMOVE_REL_PEER");
  }
}

/** `asignar-paquete` · Paquete (C08) y Nivel (C08 tarjetas: Lectura · Escritura). */
function AssignPackWindow({
  open,
  employee,
  available,
  onClose,
  onAssign,
}: {
  open: boolean;
  employee: Employee;
  available: { code: string; name: string }[];
  onClose: () => void;
  onAssign: (pack: { code: string; name: string }, access: "READ" | "WRITE") => Promise<boolean>;
}) {
  const { L } = useTexts();
  const [packCode, setPackCode] = useState("");
  const [access, setAccess] = useState<"READ" | "WRITE">("READ");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  // Cada vez que se abre, empieza con el primer paquete y Lectura.
  if (open !== shown) {
    setShown(open);
    if (open) {
      setPackCode(available[0]?.code ?? "");
      setAccess("READ");
    }
  }
  const pack = available.find((item) => item.code === packCode) ?? null;
  return (
    <Window
      open={open}
      title={L("ADM_EMP_ASSIGN_PACK")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy || !pack}
            aria-busy={busy || undefined}
            onClick={() => {
              if (!pack) return;
              setBusy(true);
              void onAssign(pack, access).then((done) => {
                setBusy(false);
                if (done) onClose();
              });
            }}
          >
            {L("ADM_EMP_ASSIGN")}
          </button>
        </>
      }
    >
      <div className={`tm-field ${styles.field}`}>
        <label htmlFor="assign-pack">{L("ADM_EMP_COL_PACK")}</label>
        <ChoiceMenu
          id="assign-pack"
          label={L("ADM_EMP_COL_PACK")}
          value={packCode}
          options={available.map((item) => ({ value: item.code, label: item.name }))}
          onChange={setPackCode}
          searchLabel={L("CFG1_SEARCH")}
          clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
          searchIcon={<ChatIcon name="search" />}
          clearIcon={<ChatIcon name="x" />}
          chevron={<ChatIcon name="chevron-down" />}
          check={<ChatIcon name="check" />}
        />
      </div>
      <span className={styles.fieldTitle}>{L("ADM_EMP_COL_LEVEL")}</span>
      <RadioCards
        label={formatText(L("ADM_EMP_LEVEL_OF"), employee.name)}
        value={access}
        onChange={(value) => setAccess(value as "READ" | "WRITE")}
        options={[
          { value: "READ", title: L("ADM_EMP_LEVEL_READ"), line: L("ADM_EMP_LEVEL_READ_LINE") },
          { value: "WRITE", title: L("ADM_EMP_LEVEL_WRITE"), line: L("ADM_EMP_LEVEL_WRITE_LINE") },
        ]}
      />
    </Window>
  );
}

/** `relacion` · Relación (Supervisa a · Trabaja como par con) y el empleado IA. */
function RelationWindow({
  open,
  employee,
  candidates,
  onClose,
  onAdd,
}: {
  open: boolean;
  employee: Employee;
  candidates: { id: number; name: string; job: string | null }[];
  onClose: () => void;
  onAdd: (otherId: number, relation: "SUPERVISES" | "PEER") => Promise<boolean>;
}) {
  const { L } = useTexts();
  const [relation, setRelation] = useState<"SUPERVISES" | "PEER">("SUPERVISES");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setRelation("SUPERVISES");
      setOther(String(candidates[0]?.id ?? ""));
    }
  }
  return (
    <Window
      open={open}
      title={L("ADM_EMP_ADD_RELATION")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy || !other}
            aria-busy={busy || undefined}
            onClick={() => {
              setBusy(true);
              void onAdd(Number(other), relation).then((done) => {
                setBusy(false);
                if (done) onClose();
              });
            }}
          >
            {L("ADM_EMP_ADD")}
          </button>
        </>
      }
    >
      <div className={styles.field}>
        <span className={styles.fieldTitle} id="rel-kind">
          {L("ADM_EMP_RELATION")}
        </span>
        <div className={styles.choice} role="radiogroup" aria-labelledby="rel-kind">
          {(["SUPERVISES", "PEER"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              className="tm-pill"
              aria-checked={relation === value ? "true" : "false"}
              aria-pressed={relation === value ? "true" : "false"}
              onClick={() => setRelation(value)}
            >
              {L(value === "SUPERVISES" ? "ADM_EMP_REL_SUPERVISES" : "ADM_EMP_REL_PEER")}
            </button>
          ))}
        </div>
      </div>
      <div className={`tm-field ${styles.field}`}>
        <label htmlFor="rel-other">{L("ADM_EMP_TAG")}</label>
        <ChoiceMenu
          id="rel-other"
          label={formatText(L("ADM_EMP_REL_OTHER_OF"), employee.name)}
          value={other}
          options={candidates.map((item) => ({
            value: String(item.id),
            label: item.job ? `${item.name} · ${item.job}` : item.name,
          }))}
          onChange={setOther}
          searchLabel={L("CFG1_SEARCH")}
          clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
          searchIcon={<ChatIcon name="search" />}
          clearIcon={<ChatIcon name="x" />}
          chevron={<ChatIcon name="chevron-down" />}
          check={<ChatIcon name="check" />}
        />
      </div>
    </Window>
  );
}

// ---- Conocimiento del empleado ----

type EmployeeKnowledge = {
  organization: OrgHead;
  employee: Employee;
  bases: { id: number; name: string | null; active: boolean }[];
  available: { id: number; name: string }[];
  policy: { enabled: boolean; audiences: string[]; terms: number[] };
  terms: Term[];
};

/**
 * Pestaña Conocimiento: Bases que consulta (C17 con «Quitar» que confirma; «Agregar base» desde un selector de las
 * activas que todavía no consulta) y Hasta dónde puede leer (C09 Usa conocimiento, C35 Audiencias y Temas). B6: los
 * temas inactivos que ya tenía se ven con «inactivo» y, como lo que la pantalla no muestra, se conservan al guardar.
 */
function KnowledgeTab({
  orgId,
  data,
  headFor,
  reload,
}: {
  orgId: number;
  data: EmployeeKnowledge;
  headFor: (primary: ReactNode) => ReactNode;
  reload: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const employee = data.employee;
  const base = `/api/admin/organizations/${orgId}/employees/${employee.id}/knowledge`;
  const [enabled, setEnabled] = useState(data.policy.enabled);
  const [chosenAudiences, setAudiences] = useState<string[]>(data.policy.audiences);
  const [chosenTerms, setTerms] = useState<number[]>(data.policy.terms);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<{ id: number; name: string } | null>(null);
  const toggle = <T,>(list: T[], value: T, on: boolean) =>
    on ? [...list, value] : list.filter((item) => item !== value);
  const write = async (url: string, body: unknown): Promise<boolean> => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(url, body);
    if (reply.status === 200 && reply.body?.saved) {
      reload();
      return true;
    }
    toast.show(replyMessage(L, reply.body));
    return false;
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(`${base}/policy`, {
      enabled,
      audiences: chosenAudiences,
      terms: chosenTerms,
      // B6 · los términos que la pantalla mostró: los demás que tuviera se conservan en el servidor.
      shownTerms: data.terms.map((term) => term.id),
    });
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(formatText(L("ADM_EMP_KNOWLEDGE_SAVED"), employee.name));
      reload();
      return;
    }
    setError(replyMessage(L, reply.body));
  };
  const saveButton = (
    <button
      type="button"
      className="tm-btn primary"
      disabled={busy}
      aria-busy={busy || undefined}
      onClick={() => void save()}
    >
      {L("ADM_PPL_SAVE")}
    </button>
  );
  const baseName = (item: { id: number; name: string | null }) => item.name ?? L("ADM_EMP_BASE_GONE");
  return (
    <>
      {headFor(saveButton)}
      <section className={ui.section} aria-labelledby="kn-bases">
        <div className={ui.sectionHead}>
          <h2 id="kn-bases" className={ui.subtitle}>
            {L("ADM_EMP_BASES")}
          </h2>
          <button
            type="button"
            className={own.light}
            disabled={data.available.length === 0}
            title={data.available.length === 0 ? L("ADM_EMP_NO_BASES_TO_ADD") : undefined}
            onClick={() => setAdding(true)}
          >
            {L("ADM_EMP_ADD_BASE")}
          </button>
        </div>
        {data.bases.length === 0 ? (
          <p className={ui.sectionLine}>{L("ADM_EMP_NO_BASES")}</p>
        ) : (
          <ul className={own.rows} aria-label={L("ADM_EMP_BASES")}>
            {data.bases.map((item) => (
              <li key={item.id}>
                <span className={`${own.avatar} ${own.avatarLarge} ${own.baseIcon}`} aria-hidden="true">
                  <ChatIcon name="box" />
                </span>
                <span className={own.rowText}>
                  <span className={own.rowTitle}>
                    <span title={baseName(item)}>{baseName(item)}</span>
                    {item.active ? null : <span className="tm-tag">{L("ADM_KB_INACTIVE")}</span>}
                  </span>
                  {item.active ? null : (
                    <span className={own.rowLine}>{formatText(L("ADM_EMP_BASE_INACTIVE_LINE"), employee.name)}</span>
                  )}
                </span>
                <button
                  type="button"
                  className={own.light}
                  aria-label={formatText(L("ADM_EMP_REMOVE_BASE_FOR"), baseName(item))}
                  onClick={() => setRemoving({ id: item.id, name: baseName(item) })}
                >
                  {L("ADM_EMP_REMOVE")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className={ui.section} aria-labelledby="kn-scope">
        <h2 id="kn-scope" className={ui.subtitle}>
          {L("ADM_EMP_SCOPE")}
        </h2>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className={own.group} style={{ marginTop: 12 }}>
          <Switch
            title={L("ADM_EMP_USES_KNOWLEDGE")}
            line={L(enabled ? "ADM_EMP_USES_KNOWLEDGE_ON" : "ADM_EMP_USES_KNOWLEDGE_OFF")}
            checked={enabled}
            onChange={setEnabled}
          />
        </div>
        <div className={own.group} role="group" aria-labelledby="kn-aud">
          <span id="kn-aud" className={own.groupTitle}>
            {L("ADM_EMP_AUDIENCES")}
          </span>
          <div className={own.checks}>
            {audiences.map((value) => (
              <Check
                key={value}
                checked={chosenAudiences.includes(value)}
                onChange={(on) => setAudiences(toggle(chosenAudiences, value, on))}
              >
                {L(audienceKey[value]!)}
              </Check>
            ))}
          </div>
        </div>
        <div className={own.group} role="group" aria-labelledby="kn-terms">
          <span id="kn-terms" className={own.groupTitle}>
            {L("ADM_EMP_TOPICS")}
          </span>
          {data.terms.length === 0 ? (
            <p className={ui.sectionLine}>
              {L("ADM_KB_NO_TERMS")}{" "}
              <a className="tm-link" href={`/Organizations/ConocimientoTerminos?id=${orgId}`}>
                {L("ADM_KB_TERMS")}
              </a>
            </p>
          ) : (
            <div className={own.checks}>
              {data.terms.map((term) => (
                <TermCheck
                  key={term.id}
                  term={term}
                  checked={chosenTerms.includes(term.id)}
                  onChange={(on) => setTerms(toggle(chosenTerms, term.id, on))}
                />
              ))}
            </div>
          )}
          <small className={own.help}>{L("ADM_EMP_TOPICS_HELP")}</small>
        </div>
      </section>
      <MobileActions>{saveButton}</MobileActions>
      <AddBaseWindow
        open={adding}
        available={data.available}
        onClose={() => setAdding(false)}
        onAdd={async (item) => {
          const done = await write(`${base}/bases`, { knowledgeBaseId: item.id });
          if (done) toast.show(formatText(L("ADM_EMP_BASE_ADDED"), item.name));
          return done;
        }}
      />
      <ConfirmWindow
        open={!!removing}
        title={formatText(L("ADM_EMP_REMOVE_BASE_TITLE"), removing?.name ?? "")}
        line={formatText(L("ADM_EMP_REMOVE_BASE_LINE"), employee.name)}
        action={L("ADM_EMP_REMOVE")}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return true;
          const item = removing;
          const done = await write(`${base}/bases/${item.id}/remove`, {});
          if (done)
            toast.show(L("ADM_EMP_BASE_REMOVED"), {
              label: L("ADM_UNDO"),
              run: () => void write(`${base}/bases`, { knowledgeBaseId: item.id }),
            });
          return done;
        }}
      />
    </>
  );
}

/** C35 · casilla de un término: nombre, su tipo en gris y «inactivo» si lo está (B6: se conserva). */
export function TermCheck({
  term,
  checked,
  onChange,
}: {
  term: Term;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const { L } = useTexts();
  return (
    <Check checked={checked} onChange={onChange}>
      {term.name}{" "}
      <span className={own.kind}>
        · {L(termKindKey[term.kind] ?? "ADM_KB_TERM_TOPIC")}
        {term.active ? "" : ` · ${L("ADM_KB_INACTIVE_LOWER")}`}
      </span>
    </Check>
  );
}

/** `agregar-base` · solo bases activas que todavía no consulta (C08). */
function AddBaseWindow({
  open,
  available,
  onClose,
  onAdd,
}: {
  open: boolean;
  available: { id: number; name: string }[];
  onClose: () => void;
  onAdd: (item: { id: number; name: string }) => Promise<boolean>;
}) {
  const { L } = useTexts();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) setValue(String(available[0]?.id ?? ""));
  }
  const item = available.find((base) => String(base.id) === value) ?? null;
  return (
    <Window
      open={open}
      title={L("ADM_EMP_ADD_BASE")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy || !item}
            aria-busy={busy || undefined}
            onClick={() => {
              if (!item) return;
              setBusy(true);
              void onAdd(item).then((done) => {
                setBusy(false);
                if (done) onClose();
              });
            }}
          >
            {L("ADM_EMP_ADD")}
          </button>
        </>
      }
    >
      <div className={`tm-field ${styles.field}`}>
        <label htmlFor="add-base">{L("ADM_KB_BASE")}</label>
        <ChoiceMenu
          id="add-base"
          label={L("ADM_KB_BASE")}
          value={value}
          options={available.map((base) => ({ value: String(base.id), label: base.name }))}
          onChange={setValue}
          describedBy="add-base-help"
          searchLabel={L("CFG1_SEARCH")}
          clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
          searchIcon={<ChatIcon name="search" />}
          clearIcon={<ChatIcon name="x" />}
          chevron={<ChatIcon name="chevron-down" />}
          check={<ChatIcon name="check" />}
        />
        <small id="add-base-help" className="tm-field-help">
          {L("ADM_EMP_ADD_BASE_HELP")}
        </small>
      </div>
    </Window>
  );
}
